// routeTerrainProfile.ts — Client-side fetcher for /api/elevation/path.
//
// Calls the server endpoint to get evenly-spaced terrain samples along
// a multi-waypoint route. Result shape mirrors the endpoint:
//
//   sampleRouteProfile(waypoints, { stepNm, maxSamples, signal })
//     -> { samples, source, cache, fetchedAt }
//
// Adds an in-memory dedupe + memo layer so two callers asking for the
// same route+step combination within a session share a single network
// round-trip.

export interface ProfileSample {
  lat: number;
  lon: number;
  distNm: number;
  elevFt: number | null;
}

export interface ProfileSource {
  primary: string;
  fallbackHits: number;
}

export interface ProfileResult {
  ok: true;
  samples: ProfileSample[];
  source: ProfileSource;
  cache: { gridHits: number; networkHits: number; networkPoints: number };
  fetchedAt: number;
}

export interface ProfileWaypoint {
  /** Optional ident — passed through unchanged so the chart can label
   *  waypoint markers along the X axis. */
  ident?: string;
  lat: number;
  lon: number;
}

export interface SampleOpts {
  /** Sampling step in nautical miles. Default 0.5 NM. */
  stepNm?: number;
  /** Max samples to request from the server (cap 600). */
  maxSamples?: number;
  /** Abort signal — pass-through to fetch. */
  signal?: AbortSignal;
}

// ── In-memory dedupe + memo ───────────────────────────────────────────────

/**
 * Cache key derived from the route shape + sampling step. We round
 * coordinates to 6 decimal places (~0.1 m) so callers can pass slightly
 * jittery coordinates (e.g. live GPS smoothing) without thrashing.
 */
function memoKey(wp: ProfileWaypoint[], stepNm: number): string {
  const parts = wp.map((w) => `${w.lat.toFixed(6)},${w.lon.toFixed(6)}`).join('|');
  return `${parts}@${stepNm.toFixed(3)}`;
}

// Session-lifetime memoised results.
const memo = new Map<string, ProfileResult>();
// In-flight promises so concurrent callers share a single round-trip.
const inFlight = new Map<string, Promise<ProfileResult>>();

/**
 * Fetch evenly-spaced terrain elevation samples along the great-circle
 * path defined by `waypoints`. Returns null if the waypoint list is
 * insufficient (need >=2) or the server returns a non-ok response.
 *
 * Pass `signal` to cancel; concurrent callers with the same key share
 * the same network request \u2014 cancelling one of them doesn't cancel
 * the underlying fetch.
 */
export async function sampleRouteProfile(
  waypoints: ProfileWaypoint[],
  opts: SampleOpts = {},
): Promise<ProfileResult | null> {
  if (!Array.isArray(waypoints) || waypoints.length < 2) return null;
  // Sanitize: drop entries without finite numbers.
  const clean: ProfileWaypoint[] = [];
  for (const w of waypoints) {
    if (!w || typeof w.lat !== 'number' || typeof w.lon !== 'number') continue;
    if (!Number.isFinite(w.lat) || !Number.isFinite(w.lon)) continue;
    if (Math.abs(w.lat) > 90 || Math.abs(w.lon) > 180) continue;
    clean.push({ ident: w.ident, lat: w.lat, lon: w.lon });
  }
  if (clean.length < 2) return null;

  const stepNm = typeof opts.stepNm === 'number' && opts.stepNm > 0.05 && opts.stepNm < 50
    ? opts.stepNm
    : 0.5;
  const maxSamples = typeof opts.maxSamples === 'number' && opts.maxSamples > 1
    ? Math.min(600, Math.floor(opts.maxSamples))
    : 600;

  const key = memoKey(clean, stepNm);
  const cached = memo.get(key);
  if (cached) return cached;

  const existing = inFlight.get(key);
  if (existing) return existing;

  const body = JSON.stringify({
    waypoints: clean.map((w) => ({ lat: w.lat, lon: w.lon })),
    stepNm,
    maxSamples,
  });

  const p = (async (): Promise<ProfileResult> => {
    const r = await fetch('/api/elevation/path', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: opts.signal,
    });
    if (!r.ok) {
      throw new Error(`elevation HTTP ${r.status}`);
    }
    const data = (await r.json()) as {
      ok?: boolean;
      samples?: ProfileSample[];
      source?: ProfileSource;
      cache?: { gridHits: number; networkHits: number; networkPoints: number };
      error?: string;
    };
    if (!data.ok || !Array.isArray(data.samples)) {
      throw new Error(data.error ?? 'elevation response not ok');
    }
    const out: ProfileResult = {
      ok: true,
      samples: data.samples,
      source: data.source ?? { primary: 'unknown', fallbackHits: 0 },
      cache: data.cache ?? { gridHits: 0, networkHits: 0, networkPoints: 0 },
      fetchedAt: Date.now(),
    };
    memo.set(key, out);
    inFlight.delete(key);
    return out;
  })();

  inFlight.set(key, p);

  try {
    return await p;
  } catch (e) {
    inFlight.delete(key);
    // Don't propagate abort errors as a hard failure \u2014 caller knows.
    if (e instanceof Error && e.name === 'AbortError') return null;
    return null;
  }
}

/**
 * Compute per-sample AGL clearance (cruise altitude minus terrain).
 * Returns a new array; null elevations propagate to null AGL.
 */
export function computeAgl(
  samples: ProfileSample[],
  cruiseAltFt: number | null,
): Array<ProfileSample & { aglFt: number | null }> {
  if (cruiseAltFt == null || !Number.isFinite(cruiseAltFt)) {
    return samples.map((s) => ({ ...s, aglFt: null }));
  }
  return samples.map((s) => ({
    ...s,
    aglFt: s.elevFt == null ? null : cruiseAltFt - s.elevFt,
  }));
}

/** Min AGL clearance across the whole profile. null if all samples are null. */
export function minAglFt(
  samples: ProfileSample[],
  cruiseAltFt: number | null,
): number | null {
  if (cruiseAltFt == null) return null;
  let m: number | null = null;
  for (const s of samples) {
    if (s.elevFt == null) continue;
    const agl = cruiseAltFt - s.elevFt;
    if (m == null || agl < m) m = agl;
  }
  return m;
}

/** Highest terrain along the profile in ft MSL. */
export function maxTerrainFt(samples: ProfileSample[]): number | null {
  let m: number | null = null;
  for (const s of samples) {
    if (s.elevFt == null) continue;
    if (m == null || s.elevFt > m) m = s.elevFt;
  }
  return m;
}

// Exported for tests.
export const __testables__ = {
  memoKey,
  memo,
  inFlight,
};
